import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { marked } from 'marked';
import { GAMES } from '../../games/registry';
import { RULES_FILES, describedRules } from '../../games/rules';
import { generateLobbySlug } from '../../utils/lobbyUtils';

// A game's rules for players: its RULES.md if it has one, otherwise its built-in rules text (the same
// text the game itself uses), one section per ruleset.

const CSS = `
.rules { line-height: 1.6; }
.rules h1 { font-size: 2rem; font-weight: 700; color: #e9c46a; margin: 0 0 .5rem; }
.rules h2 { font-size: 1.4rem; font-weight: 700; color: #e9c46a; margin: 2rem 0 .6rem; }
.rules h3 { font-size: 1.15rem; font-weight: 700; margin: 1.5rem 0 .5rem; }
.rules p { margin: 0 0 .8rem; }
.rules ul, .rules ol { margin: 0 0 .9rem 1.4rem; }
.rules ul { list-style: disc; } .rules ol { list-style: decimal; }
.rules li { margin: .25rem 0; }
.rules strong { color: #fff; }
.rules table { border-collapse: collapse; margin: .4rem 0 1.1rem; width: 100%; font-size: .95rem; display: block; overflow-x: auto; }
.rules th, .rules td { border: 1px solid #7A7C8E; padding: .4rem .6rem; text-align: left; vertical-align: top; }
.rules th { background: #4A4C5A; }
.rules img { display: block; max-width: 100%; height: auto; margin: .4rem 0 1rem; border-radius: 10px; }
.rules-described p { white-space: pre-wrap; margin: 0 0 .5rem; }
`;

export default function GameRulesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const game = GAMES.find(g => g.id === id);
  const file = id ? RULES_FILES[id] : undefined;
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!file) return;
    fetch(file)
      .then(r => (r.ok ? r.text() : Promise.reject(r.status)))
      .then(md => setHtml(marked.parse(md.replace(/<!--[\s\S]*?-->/g, '')) as string))
      .catch(() => setFailed(true));
  }, [file]);

  if (!game) {
    return (
      <div className="min-h-screen bg-payne-grey-dark text-white p-8">
        <p>No game called “{id}”.</p>
        <button className="underline" onClick={() => navigate('/explore')}>See all games</button>
      </div>
    );
  }

  const described = file && !failed ? [] : describedRules(game);
  return (
    <div className="min-h-screen bg-payne-grey-dark text-white">
      <style>{CSS}</style>
      <header className="p-4 sm:p-6 border-b border-payne-grey">
        <div className="max-w-3xl mx-auto flex flex-wrap gap-3 items-center justify-between">
          <button onClick={() => navigate('/explore')} className="text-gray-300 hover:text-white">← All games</button>
          <button
            onClick={() => navigate(`/room/${generateLobbySlug()}?game=${game.id}`)}
            className="bg-lion hover:bg-lion-light text-payne-grey-dark py-2 px-4 rounded-lg font-semibold"
          >
            Create a room
          </button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 rules">
        {game.status !== 'playable' && (
          <p className="mb-6 px-4 py-3 rounded-lg bg-amber-400/15 text-amber-200">In development: this game is still being designed, so its rules here are rough and may change.</p>
        )}
        {file && !failed ? (
          html === null ? <p className="text-gray-400">Loading the rules…</p> : <div dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <>
            <h1>{game.name}</h1>
            <p className="text-gray-300">{game.description}</p>
            {game.referenceUrl && <p><a className="underline" href={game.referenceUrl} target="_blank" rel="noreferrer">Full rules reference ↗</a></p>}
            {described.map(s => (
              <section key={s.title} className="rules-described">
                {described.length > 1 && <h2>{s.title}</h2>}
                {s.text.split('\n').map((line, i) => <p key={i}>{line}</p>)}
              </section>
            ))}
            {!described.length && <p className="text-gray-400">This game's rules haven't been written up yet.</p>}
          </>
        )}
      </main>
    </div>
  );
}
